import { refreshToken } from 'actions/authentication/authentication';
import { logout } from 'actions/entities/user/user';
import {
  AUTH_LOCALSTORAGE_KEY,
  IMPERSONATE_USER_SUCCESS,
  LOGIN_EXPIRATION_IN_MINUTES,
  LOGIN_USER_SUCCESS,
  LOGOUT_USER_REQUEST,
  LOGOUT_USER_SUCCESS,
  REFRESH_TOKEN,
} from 'constants/user';
import isEmpty from 'lodash/isEmpty';
import type moment from 'moment';
import type { RootState } from 'reducers/types';
import type { AnyAction, Middleware, MiddlewareAPI } from 'redux';
import {
  getRefreshToken,
  isLoggedIn,
} from 'selectors/entities/authentication/authentication';
import { isEmbedded } from 'selectors/ui/ui';
import type { PayexDispatch } from 'store/configureStore';
import { future, isBefore, MINUTES, now } from 'utils/date/date';
import { localStorage } from 'utils/storage';
import { getStoredRefreshToken } from '../../storage';

type AccessToken = {
  accessToken: string;
  idleTimeout: string;
};

type Store = MiddlewareAPI<PayexDispatch, RootState>;

const Authentication: Middleware<unknown, RootState> =
  store => next => async (action: unknown) => {
    const typedAction = action as AnyAction;
    const accessToken = getUserAuthentication();

    if (isLoginSuccess(typedAction)) {
      setAccessToken(typedAction);
    }
    if (shouldLogOut(store, typedAction, accessToken)) {
      // @ts-expect-error for some reason we cannot type the action
      await store.dispatch(logout());
    }
    if (isLoggedInOnAnotherTab(store, typedAction)) {
      // @ts-expect-error for some reason we cannot type the action
      store.dispatch(refreshToken());
    }

    updateExpiration(accessToken);
    return next(action);
  };

const getUserAuthentication = () => {
  const userAuthentication = localStorage.getItem(AUTH_LOCALSTORAGE_KEY);

  if (isEmpty(userAuthentication)) {
    return {};
  }

  return JSON.parse(userAuthentication as string);
};

const expiration = () => {
  return future(
    LOGIN_EXPIRATION_IN_MINUTES,
    MINUTES as moment.DurationInputArg2,
  );
};

const setAccessToken = (action: AnyAction) => {
  const {
    payload: { accessToken },
  } = action;
  const token = JSON.stringify({
    accessToken,
    idleTimeout: expiration(),
  });

  localStorage.setItem(AUTH_LOCALSTORAGE_KEY, token);
};

const isExpired = (accessToken?: AccessToken) => {
  if (isEmpty(accessToken)) {
    return false;
  }
  const currentDate = now();
  const expirationDate = accessToken.idleTimeout;
  return isBefore(expirationDate, currentDate);
};

const isLoginSuccess = (action: AnyAction) =>
  [LOGIN_USER_SUCCESS, IMPERSONATE_USER_SUCCESS].includes(action.type);

const updateExpiration = (accessToken?: AccessToken) => {
  if (!isEmpty(accessToken)) {
    accessToken.idleTimeout = expiration();
    localStorage.setItem(AUTH_LOCALSTORAGE_KEY, JSON.stringify(accessToken));
  }
};

const hasBeenLoggedOutPreviously = (state: RootState, action: AnyAction) => {
  if (action.type === LOGOUT_USER_SUCCESS) {
    return false;
  }
  if (action.type === LOGOUT_USER_REQUEST) {
    return false;
  }

  const hasStoredRefreshToken = !!getStoredRefreshToken();
  const hasRefreshToken = getRefreshToken(state);
  const loggedIn = isLoggedIn(state);

  return !!(!hasStoredRefreshToken && (hasRefreshToken || loggedIn));
};

const shouldLogOut = (
  store: Store,
  action: AnyAction,
  accessToken: AccessToken,
) => {
  const state = store.getState();
  if (isEmbedded(state)) {
    return false;
  }

  return (
    hasBeenLoggedOutPreviously(state, action) ||
    (!isLogoutSuccess(action) && isExpired(accessToken))
  );
};

const isLoggedInOnAnotherTab = (store: Store, action: AnyAction) => {
  if (action.type === REFRESH_TOKEN) {
    return false;
  }

  const state = store.getState();
  const hasRefreshToken = getRefreshToken(state);
  const isNotLoggedIn = !isLoggedIn(state);

  return hasRefreshToken && isNotLoggedIn;
};

const isLogoutSuccess = ({ type }: { type: string }) => {
  const logoutActions = [LOGOUT_USER_SUCCESS, LOGOUT_USER_REQUEST];
  return logoutActions.includes(type);
};

export { Authentication };
